import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Calculator, TrendingDown, DollarSign, Building, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const Tax = () => {
  const services = [
    {
      icon: Calculator,
      title: 'Strategic Tax Planning',
      description: 'Proactive strategies to minimize tax liability and maximize wealth retention throughout the year.',
    },
    {
      icon: DollarSign,
      title: 'Tax-Loss Harvesting',
      description: 'Optimize investment returns through strategic realization of capital losses to offset gains.',
    },
    {
      icon: TrendingDown,
      title: 'Tax-Efficient Investing',
      description: 'Investment strategies designed to minimize tax impact while maximizing after-tax returns.',
    },
    {
      icon: Building,
      title: 'Business Tax Consulting',
      description: 'Comprehensive tax planning for business owners and entrepreneurs to optimize structure and deductions.',
    },
    {
      icon: Users,
      title: 'Family Tax Strategies',
      description: 'Multi-generational tax planning to preserve wealth across family members and future generations.',
    },
    {
      icon: FileText,
      title: 'Estate & Gift Tax Planning',
      description: 'Minimize estate and gift tax exposure through strategic planning with our in-house experts.',
    },
  ];

  const benefits = [
    'In-house CPAs with extensive experience in affluent taxation',
    'Year-round tax planning, not just seasonal preparation',
    'Coordination with asset management and estate planning teams',
    'Proactive identification of tax-saving opportunities',
    'IRS audit support and representation',
    'State and federal tax compliance',
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Tax Planning Services",
    "provider": {
      "@type": "FinancialService",
      "name": "Paracca Financial Group"
    },
    "areaServed": "nationwide in all 50 states",
    "description": "Professional tax planning and CPA services for individuals in nationwide in all 50 states."
  };

  return (
    <>
      <SEO 
        title="Tax Services | In-House CPAs | Paracca Financial Group - Nationwide Service"
        description="Expert tax preparation, planning and CPA services nationwide. Strategic tax strategies for individuals and families in all 50 states. Year-round tax optimization."
        keywords="tax services sarasota fl, cpa sarasota, tax advisor sarasota, tax strategies florida, wealth tax planning, tax preparation sarasota"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85')`,
          }}
        >
          <div className="absolute inset-0 bg-[#0a69b2] opacity-70"></div>
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-bellefair text-4xl md:text-6xl  text-white mb-6 uppercase">
              Tax Services
            </h1>
            <p className="font-montserrat text-lg md:text-xl text-gray-100 leading-relaxed">
              Comprehensive tax preparation, planning, and optimization with our in-house CPAs
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
              Complete Tax Solutions
            </h2>
            <p className="font-montserrat text-lg text-gray-600 leading-relaxed mb-8">
              At Paracca Financial Group, our in-house CPAs provide comprehensive tax services including preparation, planning, and optimization designed to minimize your tax burden and maximize wealth retention. Unlike traditional tax preparation services, we take a proactive, year-round approach that integrates seamlessly with your overall financial strategy—serving clients nationwide, all 50 states, and nationwide.
            </p>
            <p className="font-montserrat text-lg text-gray-600 leading-relaxed mb-8">
              Serving individuals and families nationwide and throughout all 50 states, our tax professionals stay ahead of changing tax laws to identify opportunities that preserve and grow your wealth. With clients in all 50 states, we provide comprehensive tax services wherever you're located.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Our Tax Planning Services
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Comprehensive strategies to optimize your tax position
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={service.title} className="hover:shadow-xl transition-shadow duration-300 border-2">
                  <CardContent className="p-6">
                    <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 uppercase">
                      {service.title}
                    </h3>
                    <p className="font-montserrat text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
                The Paracca Tax Advantage
              </h2>
              <p className="font-montserrat text-lg text-gray-600 mb-8 leading-relaxed">
                Our integrated approach to tax planning sets us apart. With in-house CPAs working alongside our asset management and estate planning teams, we deliver coordinated strategies that optimize your entire financial picture.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0a69b2] flex items-center justify-center mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-montserrat text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1710492341412-8b3aee7e70a6"
                alt="Tax Preparation Nationwide Service - CPA Tax Planning Services Nationwide"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0a69b2] text-white p-8 rounded-lg shadow-xl max-w-xs">
                <p className="font-bellefair text-3xl  mb-2">Year-Round</p>
                <p className="font-montserrat text-sm">Tax planning and optimization, not just seasonal preparation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
            Integrated Financial Planning
          </h2>
          <p className="font-montserrat text-lg text-gray-600 leading-relaxed mb-8">
            Tax planning doesn't exist in a vacuum. Our in-house CPAs work closely with your asset management and estate planning teams to ensure every financial decision is tax-optimized. This holistic approach is what makes Paracca Financial Group truly unique nationwide.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <TrendingDown className="text-[#0a69b2] mx-auto mb-4" size={40} />
              <h3 className="font-bellefair text-lg  text-[#0a69b2] mb-2 uppercase">Tax-Efficient Investing</h3>
              <p className="font-montserrat text-sm text-gray-600">Coordinated with asset management</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <FileText className="text-[#0a69b2] mx-auto mb-4" size={40} />
              <h3 className="font-bellefair text-lg  text-[#0a69b2] mb-2 uppercase">Estate Tax Planning</h3>
              <p className="font-montserrat text-sm text-gray-600">Aligned with estate strategy</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Calculator className="text-[#0a69b2] mx-auto mb-4" size={40} />
              <h3 className="font-bellefair text-lg  text-[#0a69b2] mb-2 uppercase">Proactive Planning</h3>
              <p className="font-montserrat text-sm text-gray-600">Year-round optimization</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a69b2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  mb-6 uppercase">
            Reduce Your Tax Burden Today
          </h2>
          <p className="font-montserrat text-lg mb-8 leading-relaxed text-gray-100">
            Schedule a consultation with our in-house CPAs to discuss your tax planning needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-[#0a69b2] hover:bg-gray-100 font-montserrat font-semibold px-8 py-6 transition-all duration-300"
              asChild
            >
              <a href="tel:941-545-9057">
                Call 941-545-9057
              </a>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0a69b2] font-montserrat font-semibold px-8 py-6"
              asChild
            >
              <Link to="/">
                Learn More
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Request a Tax Planning Consultation
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Let our CPAs help you minimize your tax liability
            </p>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
};

export default Tax;
